import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CronExpressionBuilder, { validateCronExpression } from "../../components/CronExpressionBuilder";
import BedrockIdeaService from "./BedrockIdeaService";

const DEFAULT_CRON = "0 0 0 * * ?";

export default function BiContSchedulerDialog({ open, onClose, onSuccess, onError }) {
    const [schedulerConfig, setSchedulerConfig] = useState({
        enabled: false,
        cron: DEFAULT_CRON,
        roundRobin: false,
        lastIndex: null,
        countDown: 0
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setLoading(true);
            BedrockIdeaService.getRoundRobinSchedulerConfig()
                .then((result) => {
                    setLoading(false);
                    if (result.success && result.data) {
                        const d = result.data;
                        setSchedulerConfig({
                            enabled: d.enabled ?? false,
                            cron: d.cron || DEFAULT_CRON,
                            roundRobin: d.roundRobin ?? false,
                            lastIndex: d.lastIndex ?? null,
                            countDown: d.countDown ?? 0
                        });
                    } else {
                        onError?.(result.error || "Failed to load scheduler config");
                        onClose?.();
                    }
                })
                .catch((err) => {
                    setLoading(false);
                    onError?.(err.message || "Failed to load scheduler config");
                    onClose?.();
                });
        }
    }, [open]);

    const handleSave = async () => {
        const validation = validateCronExpression(schedulerConfig.cron);
        if (!validation.valid) {
            onError?.(validation.error);
            return;
        }
        const countDown = Number(schedulerConfig.countDown);
        if (isNaN(countDown) || countDown < 0) {
            onError?.("Count down must be a non-negative number");
            return;
        }
        setSaving(true);
        const result = await BedrockIdeaService.updateRoundRobinSchedulerConfig(schedulerConfig);
        setSaving(false);
        if (result.success) {
            onSuccess?.();
            onClose?.();
        } else {
            onError?.(result.error || "Failed to update scheduler config");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Continuous Scheduler</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Typography color="text.secondary">Loading...</Typography>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={schedulerConfig.enabled}
                                    onChange={(e) =>
                                        setSchedulerConfig((prev) => ({ ...prev, enabled: e.target.checked }))
                                    }
                                    color="primary"
                                />
                            }
                            label="Enabled"
                        />
                        <CronExpressionBuilder
                            value={schedulerConfig.cron}
                            onChange={(cron) => setSchedulerConfig((prev) => ({ ...prev, cron }))}
                        />
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap" }}>
                            <TextField
                                label="Count Down"
                                type="number"
                                size="small"
                                value={schedulerConfig.countDown ?? 0}
                                onChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    setSchedulerConfig((prev) => ({ ...prev, countDown: isNaN(v) ? 0 : v }));
                                }}
                                inputProps={{ min: 0, step: 1 }}
                                helperText="Number of runs before switching to next idea (0 = use cron only)"
                                sx={{ minWidth: 120 }}
                            />
                            <ToggleButtonGroup
                                value={[10, 20, 50, 100].includes(schedulerConfig.countDown) ? schedulerConfig.countDown : null}
                                exclusive
                                onChange={(_, value) => {
                                    if (value != null) setSchedulerConfig((prev) => ({ ...prev, countDown: value }));
                                }}
                                size="small"
                                sx={{ mt: 0.5 }}
                            >
                                {[10, 20, 50, 100].map((n) => (
                                    <ToggleButton key={n} value={n} aria-label={`Set count down to ${n}`}>
                                        {n}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>
                        {schedulerConfig.lastIndex != null && (
                            <Typography variant="body2" color="text.secondary">
                                Last used index: {schedulerConfig.lastIndex}
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            {!loading && (
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}
